// ink-component
import React, { ReactNode } from 'react';
import flattenChildren from 'react-keyed-flatten-children';

import getGlobalProps from '../../helpers/constants/global-props';
import { SpacingType } from '../../helpers/constants/layoutPropTypes';

import Frame from '../Frame/Frame';
import StyledBox from '../StyledBox/StyledBox';
import { LayoutHTMLType } from '../_common/Types/CustomTypes';

export interface TilesProps extends React.HTMLProps<LayoutHTMLType> {
  /** Contents. */
  children?: ReactNode;
  /** Set gap spacing between children. See [spacing components](/guides/layout/#spacing-components) for details.  */
  spacing?: SpacingType;
  /** Number of columns per row. May be set as an [array](/guides/layout/#advanced) for responsive behavior */
  columns?: number | number[];
  /** Tag that will be rendered by the Component. */
  as?: 'div' | 'ol' | 'ul' | 'span';
  /** Tag that will be rendered by the Component's immediate children. */
  childrenAs?: string;
}

const globalProps = getGlobalProps({});

/**
 * @purpose Layout items in a column grid
 * @group Layout
 * @position 3
 * @categories layout
 * @aka grid
 * @HTMLType LayoutHTMLType
 */
function Tiles(props: TilesProps) {
  const { className, style, children, spacing, columns, as, childrenAs, ...otherProps } = props;

  const normalizeColumnsProp = columns => {
    if (typeof columns === 'object') {
      return columns.map(column => `repeat(${column}, 1fr);`);
    }

    return `repeat(${columns}, 1fr);`;
  };

  return (
    <Frame>
      <StyledBox
        otherProps={otherProps}
        component={as}
        display="grid"
        gridGap={spacing}
        gridTemplateColumns={normalizeColumnsProp(columns)}
        gridAutoRows="auto"
        {...globalProps}
      >
        {React.Children.map(flattenChildren(children), child => (
          <StyledBox minWidth={0} height="100%" component={childrenAs}>
            {child}
          </StyledBox>
        ))}
      </StyledBox>
    </Frame>
  );
}

Tiles.defaultProps = {
  children: undefined,
  spacing: 'gutter',
  columns: 1,
  as: 'div',
  childrenAs: 'div',
};

Tiles.displayName = 'Tiles';

export default Tiles;
